import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { accountService } from '../services/accountService';
import {
  ICreateBrandsProps,
  IRemoveProfileProps,
  IRemoveSellerProps,
  IRemoveVendorProps,
  IUpdateBrandProps,
} from '@/types/account';
import { message } from 'antd';

// create new account
export const useCreateAccount = (): UseMutationResult<
  void,
  Error,
  ICreateBrandsProps
> => {
  const queryClient = useQueryClient(); // Initialize queryClient
  return useMutation<void, Error, ICreateBrandsProps>({
    mutationFn: async (payload: ICreateBrandsProps) => {
      return await accountService.createAccount(payload);
    },
    onSuccess: () => {
      message.success('Account created successfully!');
      queryClient.invalidateQueries({ queryKey: ['accounts'] }); // Adjusted to match the expected type
    },
    onError: () => {
      message.error('Failed to create account.');
    },
  });
};

// update an account
export const useUpdateProfile = (): UseMutationResult<
  void,
  Error,
  ICreateBrandsProps & { id: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ICreateBrandsProps & { id: string }>({
    mutationFn: async ({ id, ...payload }) => {
      await accountService.updateAccount(id, payload);
    },
    onSuccess: () => {
      message.success('Account updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: () => {
      message.error('Failed to update accounts.');
    },
  });
};

// Get all the accounts for the current user
export const useAccounts = () => {
  return useQuery({
    queryKey: ['accounts'],
    // Use the service function to fetch accounts
    queryFn: accountService.getAccounts,
  });
};

// deleteAccount
export const useDeleteAccount = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient(); // Initialize queryClient
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await accountService.deleteAccount(id); // Make sure this function exists in accountService
    },
    onSuccess: () => {
      message.success('Account deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: () => {
      message.error('Failed to delete account.');
    },
  });
};

// add a profile to an account
export const useUpdateBrand = (): UseMutationResult<
  void,
  Error,
  IUpdateBrandProps
> => {
  const queryClient = useQueryClient(); // Initialize queryClient

  return useMutation<void, Error, IUpdateBrandProps>({
    mutationFn: async ({ id, profileId, url }: IUpdateBrandProps) => {
      await accountService.updateAccountWithProfiles(id, { profileId, url });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: () => {
      message.error('Failed to update brand.');
    },
  });
};

// remove a profile from an account
export const useRemoveProfile = (): UseMutationResult<
  void,
  Error,
  IRemoveProfileProps
> => {
  const queryClient = useQueryClient(); // Initialize queryClient

  return useMutation({
    mutationFn: async ({ id, profileId }: IRemoveProfileProps) => {
      if (!id || !profileId) {
        throw new Error('Both account ID and profile ID are required.');
      }

      await accountService.removeProfileFromAccount(id, profileId);
    },
    onSuccess: () => {
      message.success('Profile removed successfully!');

      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to remove profile.');
    },
  });
};
export const useRemoveSeller = (): UseMutationResult<
  void,
  Error,
  IRemoveSellerProps
> => {
  const queryClient = useQueryClient(); // Initialize queryClient

  return useMutation({
    mutationFn: async ({ id, sellerId }: IRemoveSellerProps) => {
      if (!id || !sellerId) {
        throw new Error('Both account ID and seller ID are required.');
      }

      await accountService.removeSellerFromAccount(id, sellerId);
    },
    onSuccess: () => {
      message.success('Seller removed successfully!');

      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to remove profile.');
    },
  });
};
export const useRemoveVendor = (): UseMutationResult<
  void,
  Error,
  IRemoveVendorProps
> => {
  const queryClient = useQueryClient(); // Initialize queryClient

  return useMutation({
    mutationFn: async ({ id, vendorId }: IRemoveVendorProps) => {
      if (!id || !vendorId) {
        throw new Error('Both account ID and seller ID are required.');
      }

      await accountService.removeVendorFromAccount(id, vendorId);
    },
    onSuccess: () => {
      message.success('Seller removed successfully!');

      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to remove profile.');
    },
  });
};

// Get seller and vendor accounts
export const useSellerVendorAccounts = () => {
  return useQuery({
    queryKey: ['seller-vendor-accounts'],
    queryFn: accountService.getSellerVendorAccounts,
  });
};

export const useUserByEmail = (email: string) => {
  return useQuery({
    queryKey: ['user-by-email', email],
    queryFn: () => accountService.getUserEmail(email),
    enabled: !!email,
  });
};

export const useGetRegionWiseAccounts = (region: string) => {
  return useQuery({
    queryKey: ['region-wise-accounts', region],
    queryFn: () => accountService.getRegionWiseAccounts(region),
    enabled: !!region,
  });
};

export const useGetAccountLimits = () => {
  return useQuery({
    queryKey: ['account-limits'],
    queryFn: accountService.getAccountLimits,
  });
};

export const useGetSeatLimits = () => {
  return useQuery({
    queryKey: ['seat-limits'],
    queryFn: accountService.getSeatLimits,
  });
};
